import React, { useState, type FC } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";
import L, { type LatLngBounds, type Map as LeafletMap } from "leaflet";
import { toast } from "react-toastify";
import { LeafletBarControl } from "./LeafletBarControl";
import { collectPinLatLngs } from "../utils/collectPinLatLngs";

export interface FitToObjectsControlProps {
  /** 対象の Leaflet マップ。未準備時は null */
  map: LeafletMap | null;
  /** 描画図形を保持する FeatureGroup の ref */
  drawnItemsRef: React.MutableRefObject<L.FeatureGroup>;
}

/**
 * 地図上のピンと描画図形がすべて収まる位置・ズームへ自動的に移動するコントロール。
 * 見た目は LeafletBarControl 経由で Leaflet 標準の `.leaflet-bar` に委譲する。
 */
export const FitToObjectsControl: FC<FitToObjectsControlProps> = ({
  map,
  drawnItemsRef,
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);

  /** 地図上のピンと描画図形を統合した bounds を返す。対象が無い場合は null */
  const collectObjectsBounds = (
    mapInstance: LeafletMap
  ): LatLngBounds | null => {
    const markerLatLngs = collectPinLatLngs(mapInstance);

    let mergedBounds: LatLngBounds | null =
      markerLatLngs.length > 0 ? L.latLngBounds(markerLatLngs) : null;

    drawnItemsRef.current.eachLayer((layer) => {
      if (
        "getBounds" in layer &&
        typeof (layer as L.Polygon).getBounds === "function"
      ) {
        try {
          const b = (layer as L.Polygon).getBounds();
          if (b.isValid()) {
            mergedBounds = mergedBounds ? mergedBounds.extend(b) : b;
          }
        } catch {
          // bounds 取得できない図形はスキップ
        }
      }
    });

    return mergedBounds?.isValid() ? mergedBounds : null;
  };

  const handleConfirm = (): void => {
    setDialogOpen(false);

    if (!map) {
      return;
    }

    const bounds = collectObjectsBounds(map);
    if (!bounds) {
      toast.info("ピン・図形がありません");
      return;
    }

    map.fitBounds(bounds, {
      // ピンの高さ分のオフセット。これがないとピンが画面外にはみ出す。
      paddingTopLeft: [0, 42],
      maxZoom: map.getZoom(),
      animate: false,
    });
  };

  return (
    <>
      <LeafletBarControl position="topleft">
        <a
          href="#"
          role="button"
          title="ピン・図形を画面に収める"
          aria-label="ピン・図形を画面に収める"
          onClick={(event) => {
            event.preventDefault();
            setDialogOpen(true);
          }}
        >
          <CenterFocusStrongIcon
            sx={{ fontSize: 18, verticalAlign: "middle" }}
          />
        </a>
      </LeafletBarControl>

      <Dialog
        open={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
        }}
      >
        <DialogTitle>ピン・図形を画面に収める</DialogTitle>
        <DialogContent>
          <DialogContentText>
            地図上のピンと描画図形がすべて収まる位置・ズームに移動します。よろしいですか？
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDialogOpen(false);
            }}
          >
            キャンセル
          </Button>
          <Button variant="contained" onClick={handleConfirm}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
